// The history module (#3382 milestone 1): what a view is known to miss and why its recovery
// may not read idle.
import type { RecoveryHold } from "./types.js";

export interface ConversationCoverage {
  // Committed rows known missing between two on-screen rows that a read-back could not bring
  // back (it failed, or its budget ran out), named so the screen says so (#3338 follow-up).
  lostRange?: { after: number; before: number };
  // Reasons the recovery may not be idle ("hole": the lost range), evaluated together: idle
  // only when none is left, and releasing one never clears another.
  holds: Map<RecoveryHold, "stalled" | "pending">;
  // Whether the recovery state was put there by the holds alone: only such a state follows
  // them; a pass that moves the recovery itself owns its state.
  stateFromHolds: boolean;
  // With dropped deltas owed: the debt revision whose tail was read since, turn over.
  tailReadAfterTurn?: number;
  // The last seq a turn's end named that the view has not applied yet (knowledge/124 T1).
  turnEndSeq?: number;
  // While a turn end is owed: the highest row a read served that the screen already shows by
  // shape (a reply drawn from its live stream, no row seq), so it is never applied by seq.
  shapeSeq?: number;
}

// Every conversation's coverage record (M1 slice 6b): what is known missing and why its
// recovery may not read idle. forget() drops a record whole; the delta-debt revisions are
// monotonic and survive it, so an old read can never pay a newer debt. Identity (life,
// liveLife) is not coverage and stays on the stream.
export class CoverageBook {
  private readonly records = new Map<string, ConversationCoverage>();
  private readonly debtRevisions = new Map<string, number>();

  private of(agentId: string): ConversationCoverage {
    let record = this.records.get(agentId);
    if (!record) {
      record = { holds: new Map(), stateFromHolds: false };
      this.records.set(agentId, record);
    }
    return record;
  }

  lostRange(agentId: string): { after: number; before: number } | undefined {
    return this.records.get(agentId)?.lostRange;
  }

  setLostRange(agentId: string, range: { after: number; before: number } | undefined): void {
    if (range) this.of(agentId).lostRange = range;
    else {
      const record = this.records.get(agentId);
      if (record) record.lostRange = undefined;
    }
  }

  hold(agentId: string, reason: RecoveryHold, state: "stalled" | "pending"): void {
    this.of(agentId).holds.set(reason, state);
  }

  // Whether the reason was held (and is now released).
  release(agentId: string, reason: RecoveryHold): boolean {
    return this.records.get(agentId)?.holds.delete(reason) ?? false;
  }

  isHeld(agentId: string, reason: RecoveryHold): boolean {
    return this.records.get(agentId)?.holds.has(reason) ?? false;
  }

  // The strongest state the holds ask for, or undefined when none is left.
  heldState(agentId: string): "stalled" | "pending" | undefined {
    const holds = this.records.get(agentId)?.holds;
    if (!holds || holds.size === 0) return undefined;
    return [...holds.values()].includes("stalled") ? "stalled" : "pending";
  }

  fromHolds(agentId: string): boolean {
    return this.records.get(agentId)?.stateFromHolds ?? false;
  }

  setFromHolds(agentId: string, value: boolean): void {
    if (value) this.of(agentId).stateFromHolds = true;
    else {
      const record = this.records.get(agentId);
      if (record) record.stateFromHolds = false;
    }
  }

  debtRevision(agentId: string): number {
    return this.debtRevisions.get(agentId) ?? 0;
  }

  // New debt: a new revision, and a tail read armed for the old one no longer pays.
  bumpDebt(agentId: string): void {
    this.debtRevisions.set(agentId, this.debtRevision(agentId) + 1);
    const record = this.records.get(agentId);
    if (record) record.tailReadAfterTurn = undefined;
  }

  armTailRead(agentId: string, revision: number): void {
    this.of(agentId).tailReadAfterTurn = revision;
  }

  turnEndSeq(agentId: string): number | undefined {
    return this.records.get(agentId)?.turnEndSeq;
  }

  setTurnEndSeq(agentId: string, seq: number | undefined): void {
    if (seq !== undefined) this.of(agentId).turnEndSeq = seq;
    else {
      const record = this.records.get(agentId);
      if (record) {
        record.turnEndSeq = undefined;
        record.shapeSeq = undefined;
      }
    }
  }

  shapeSeq(agentId: string): number | undefined {
    return this.records.get(agentId)?.shapeSeq;
  }

  // Counted only while a turn end is owed: it settles that end and nothing else.
  noteShapeSeq(agentId: string, seq: number): void {
    const record = this.records.get(agentId);
    if (record?.turnEndSeq === undefined) return;
    record.shapeSeq = Math.max(record.shapeSeq ?? 0, seq);
  }

  // The armed revision, disarmed.
  takeTailRead(agentId: string): number | undefined {
    const record = this.records.get(agentId);
    const armed = record?.tailReadAfterTurn;
    if (record) record.tailReadAfterTurn = undefined;
    return armed;
  }

  // Forget the record; whether its recovery state was the holds' own.
  forget(agentId: string): boolean {
    const fromHolds = this.fromHolds(agentId);
    this.records.delete(agentId);
    return fromHolds;
  }

  clear(): void {
    this.records.clear();
  }
}
